import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import {
  CONTINUOUS_CHUNK_MS,
  RECORDING_CLIP_MS,
  RECORDING_HEARTBEAT_MS,
  RECORDING_INTERVAL_MS,
  RECORDING_MODES,
  RECORDING_SLOT_TOLERANCE_MS,
  RECORDING_TAB_LOCK,
  RECORDING_TAB_STORAGE_KEY,
  SCREEN_SHARE_REMINDER_MS,
} from 'constants';
import {
  getCaptureSessionId,
  getRecordingTabId,
  supportsDisplayCapture,
} from 'utils/captureSession';
import {
  buildUploadQueueId,
  bufferFromBlob,
  enqueueRecordingUpload,
  processRecordingUploadQueue,
} from 'utils/recordingUploadQueue';
import { showScreenShareNotification } from 'utils/screenShareReminder';
import {
  useClaimRecordingMutation,
  useHeartbeatRecordingMutation,
  useReleaseRecordingMutation,
  useUploadRecordingMutation,
} from 'state/slices/wfh/wfhApiSlice';
import { showWarningToast } from 'utils/toast';

const ScreenRecordingContext = createContext({
  status: 'idle',
  startRecording: async () => {},
  stopRecording: async () => {},
  supportsCapture: false,
});

export const useScreenRecording = () => useContext(ScreenRecordingContext);

const pickRecorderMimeType = () => {
  const types = [
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
    'video/mp4',
  ];
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
    return '';
  }
  return types.find((type) => MediaRecorder.isTypeSupported(type)) || '';
};

const getOpenWfh = (wfhs = []) => wfhs.find((row) => !row.timeOut) || null;

const getDueSlotAt = (timeInAt, recordingIntervalMs, now = Date.now()) => {
  const start = new Date(timeInAt).getTime();
  const n = Math.round((now - start) / recordingIntervalMs);
  if (n < 1) {
    return null;
  }
  const slotAt = start + n * recordingIntervalMs;
  if (Math.abs(now - slotAt) > RECORDING_SLOT_TOLERANCE_MS) {
    return null;
  }
  return slotAt;
};

const msUntilNextSlot = (timeInAt, recordingIntervalMs, now = Date.now()) => {
  const start = new Date(timeInAt).getTime();
  const n = Math.floor((now - start) / recordingIntervalMs) + 1;
  const nextAt = start + Math.max(1, n) * recordingIntervalMs;
  return Math.max(250, nextAt - now);
};

const getRecordingIntervalMs = (wfh) => {
  const intervalMs = Number(wfh?.recordingIntervalMs);
  if (Number.isFinite(intervalMs) && intervalMs > 0) {
    return intervalMs;
  }
  return RECORDING_INTERVAL_MS;
};

const getRecordingMode = (wfh) => (
  wfh?.recordingMode === RECORDING_MODES.continuous
    ? RECORDING_MODES.continuous
    : RECORDING_MODES.interval
);

export const ScreenRecordingProvider = ({ children }) => {
  const wfhs = useSelector((state) => state.wfh.wfhs);
  const isPunchedOut = useSelector((state) => state.wfh.isPunchedOut);
  const openWfh = getOpenWfh(wfhs);

  const [status, setStatus] = useState('idle');
  const [claimRecording] = useClaimRecordingMutation();
  const [heartbeatRecording] = useHeartbeatRecordingMutation();
  const [releaseRecording] = useReleaseRecordingMutation();
  const [uploadRecording] = useUploadRecordingMutation();

  const streamRef = useRef(null);
  const heartbeatRef = useRef(null);
  const slotTimerRef = useRef(null);
  const continuousChunkTimerRef = useRef(null);
  const recordingRef = useRef(false);
  const recordedSlotsRef = useRef(new Set());
  const lockAbortRef = useRef(null);
  const lockReleaseRef = useRef(null);
  const statusRef = useRef(status);
  const openWfhRef = useRef(openWfh);
  const continuousRecorderRef = useRef(null);
  const partIndexRef = useRef(0);
  const chunkStartedAtRef = useRef(null);
  const recordingModeRef = useRef(RECORDING_MODES.interval);
  const stopContinuousRef = useRef(false);

  statusRef.current = status;
  openWfhRef.current = openWfh;

  const clearTimers = () => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    if (slotTimerRef.current) {
      clearTimeout(slotTimerRef.current);
      slotTimerRef.current = null;
    }
    if (continuousChunkTimerRef.current) {
      clearTimeout(continuousChunkTimerRef.current);
      continuousChunkTimerRef.current = null;
    }
  };

  const stopTracks = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const releaseTabLock = () => {
    try {
      localStorage.removeItem(RECORDING_TAB_STORAGE_KEY + '_holder');
    } catch (err) {}
    if (lockReleaseRef.current) {
      lockReleaseRef.current();
      lockReleaseRef.current = null;
    }
    if (lockAbortRef.current) {
      lockAbortRef.current.abort();
      lockAbortRef.current = null;
    }
  };

  const holdTabLock = async () => {
    const tabId = getRecordingTabId();
    try {
      const holder = localStorage.getItem(RECORDING_TAB_STORAGE_KEY + '_holder');
      if (holder && holder !== tabId) {
        throw new Error('Screen recording is already running in another tab');
      }
      localStorage.setItem(RECORDING_TAB_STORAGE_KEY + '_holder', tabId);
    } catch (err) {
      if (err.message && err.message.includes('another tab')) {
        throw err;
      }
    }

    if (navigator.locks && navigator.locks.request) {
      const abort = new AbortController();
      lockAbortRef.current = abort;
      await new Promise((resolve, reject) => {
        navigator.locks.request(RECORDING_TAB_LOCK, { signal: abort.signal }, () => {
          resolve();
          return new Promise((release) => {
            lockReleaseRef.current = release;
          });
        }).catch(reject);
      });
    }
  };

  const uploadWithRetry = useCallback(async ({
    blob,
    mimeType,
    recordingMode,
    slotAt,
    partIndex,
    durationMs,
  }) => {
    const wfh = openWfhRef.current;
    if (!wfh) {
      return;
    }

    const extension = mimeType.includes('mp4') ? 'mp4' : 'webm';
    const label = recordingMode === RECORDING_MODES.continuous
      ? `continuous-${partIndex}`
      : `recording-${slotAt}`;
    const file = new File([blob], `${label}.${extension}`, { type: mimeType.split(';')[0] });
    const formData = new FormData();
    formData.append('recording', file);
    formData.append('wfhId', wfh.id);
    formData.append('captureSessionId', getCaptureSessionId());
    formData.append('recordedAt', new Date().toISOString());
    formData.append('durationMs', String(durationMs));
    formData.append('recordingMode', recordingMode);
    if (recordingMode === RECORDING_MODES.continuous) {
      formData.append('partIndex', String(partIndex));
    }

    try {
      await uploadRecording(formData).unwrap();
    } catch (err) {
      try {
        const blobBuffer = await bufferFromBlob(blob);
        await enqueueRecordingUpload({
          id: buildUploadQueueId({
            wfhId: wfh.id,
            recordingMode,
            slotAt,
            partIndex,
          }),
          wfhId: wfh.id,
          captureSessionId: getCaptureSessionId(),
          recordedAt: new Date().toISOString(),
          durationMs,
          recordingMode,
          slotAt,
          partIndex,
          mimeType: mimeType.split(';')[0],
          blobBuffer,
        });
      } catch (queueErr) {}
    }
  }, [uploadRecording]);

  const drainUploadQueue = useCallback(async () => {
    try {
      await processRecordingUploadQueue((formData) => uploadRecording(formData).unwrap());
    } catch (err) {}
  }, [uploadRecording]);

  const uploadClip = useCallback(async (blob, mimeType, slotAt) => {
    if (recordedSlotsRef.current.has(slotAt)) {
      return;
    }
    recordedSlotsRef.current.add(slotAt);
    await uploadWithRetry({
      blob,
      mimeType,
      recordingMode: RECORDING_MODES.interval,
      slotAt,
      partIndex: null,
      durationMs: RECORDING_CLIP_MS,
    });
  }, [uploadWithRetry]);

  const uploadContinuousPart = useCallback(async (blob, mimeType, partIndex, durationMs) => {
    await uploadWithRetry({
      blob,
      mimeType,
      recordingMode: RECORDING_MODES.continuous,
      slotAt: Date.now(),
      partIndex,
      durationMs,
    });
  }, [uploadWithRetry]);

  const recordClip = useCallback(async (slotAt) => {
    const stream = streamRef.current;
    if (!stream || recordingRef.current) {
      return;
    }

    const mimeType = pickRecorderMimeType();
    let recorder;
    try {
      recorder = mimeType
        ? new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 1e6 })
        : new MediaRecorder(stream, { videoBitsPerSecond: 1e6 });
    } catch (err) {
      return;
    }

    recordingRef.current = true;
    const chunks = [];
    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size) {
        chunks.push(event.data);
      }
    };
    recorder.onstop = () => {
      recordingRef.current = false;
      const blob = new Blob(chunks, { type: recorder.mimeType || 'video/webm' });
      if (blob.size > 0) {
        uploadClip(blob, recorder.mimeType || 'video/webm', slotAt);
      }
    };

    recorder.start();
    setTimeout(() => {
      if (recorder.state !== 'inactive') {
        recorder.stop();
      }
    }, RECORDING_CLIP_MS);
  }, [uploadClip]);

  const finalizeContinuousRecorder = useCallback(() => new Promise((resolve) => {
    if (continuousChunkTimerRef.current) {
      clearTimeout(continuousChunkTimerRef.current);
      continuousChunkTimerRef.current = null;
    }
    const recorder = continuousRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      resolve();
      return;
    }
    const handleStop = () => {
      recorder.removeEventListener('stop', handleStop);
      resolve();
    };
    recorder.addEventListener('stop', handleStop);
    recorder.stop();
  }), []);

  const startContinuousChunk = useCallback(() => {
    const stream = streamRef.current;
    if (!stream || stopContinuousRef.current) {
      return;
    }

    if (continuousChunkTimerRef.current) {
      clearTimeout(continuousChunkTimerRef.current);
      continuousChunkTimerRef.current = null;
    }

    const mimeType = pickRecorderMimeType();
    let recorder;
    try {
      recorder = mimeType
        ? new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 1e6 })
        : new MediaRecorder(stream, { videoBitsPerSecond: 1e6 });
    } catch (err) {
      return;
    }

    continuousRecorderRef.current = recorder;
    recordingRef.current = true;
    chunkStartedAtRef.current = Date.now();
    const partIndex = partIndexRef.current;
    const chunks = [];

    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size) {
        chunks.push(event.data);
      }
    };

    recorder.onstop = async () => {
      if (continuousRecorderRef.current !== recorder) {
        return;
      }
      continuousRecorderRef.current = null;
      recordingRef.current = false;
      const blob = new Blob(chunks, { type: recorder.mimeType || 'video/webm' });
      if (blob.size > 0) {
        const durationMs = Math.max(0, Date.now() - (chunkStartedAtRef.current || Date.now()));
        await uploadContinuousPart(blob, recorder.mimeType || 'video/webm', partIndex, durationMs);
        partIndexRef.current += 1;
      }

      if (!stopContinuousRef.current && streamRef.current) {
        startContinuousChunk();
      }
    };

    recorder.start();
    continuousChunkTimerRef.current = setTimeout(() => {
      if (recorder.state !== 'inactive') {
        recorder.stop();
      }
    }, CONTINUOUS_CHUNK_MS);
  }, [uploadContinuousPart]);

  const scheduleSlots = useCallback(() => {
    const run = () => {
      const wfh = openWfhRef.current;
      if (!wfh?.timeInAt || !streamRef.current) {
        return;
      }
      const recordingIntervalMs = getRecordingIntervalMs(wfh);
      const dueSlot = getDueSlotAt(wfh.timeInAt, recordingIntervalMs);
      if (dueSlot) {
        recordClip(dueSlot);
      }
      slotTimerRef.current = setTimeout(run, msUntilNextSlot(wfh.timeInAt, recordingIntervalMs));
    };

    if (slotTimerRef.current) {
      clearTimeout(slotTimerRef.current);
    }
    const wfh = openWfhRef.current;
    if (!wfh?.timeInAt) {
      return;
    }
    const recordingIntervalMs = getRecordingIntervalMs(wfh);
    slotTimerRef.current = setTimeout(
      run,
      Math.min(msUntilNextSlot(wfh.timeInAt, recordingIntervalMs), 1000),
    );
  }, [recordClip]);

  const stopRecording = useCallback(async (releaseClaim = true) => {
    stopContinuousRef.current = true;
    clearTimers();
    await finalizeContinuousRecorder();
    stopTracks();
    releaseTabLock();
    recordingRef.current = false;

    if (releaseClaim) {
      try {
        await releaseRecording({ captureSessionId: getCaptureSessionId() }).unwrap();
      } catch (err) {}
    }

    await drainUploadQueue();
    setStatus((prev) => (prev === 'elsewhere' ? prev : 'idle'));
  }, [drainUploadQueue, finalizeContinuousRecorder, releaseRecording]);

  const startHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
    }
    heartbeatRef.current = setInterval(async () => {
      try {
        await heartbeatRecording({ captureSessionId: getCaptureSessionId() }).unwrap();
      } catch (err) {
        if (err?.status === 409) {
          setStatus('elsewhere');
          stopRecording(false);
        }
      }
    }, RECORDING_HEARTBEAT_MS);
  }, [heartbeatRecording, stopRecording]);

  const startRecording = useCallback(async () => {
    const wfh = openWfhRef.current;
    if (!wfh?.id) {
      return;
    }
    if (!supportsDisplayCapture()) {
      setStatus('unsupported');
      return;
    }

    try {
      await holdTabLock();
    } catch (err) {
      showWarningToast(err.message || 'Screen recording is already running in another tab');
      return;
    }

    let claimResponse;
    try {
      claimResponse = await claimRecording({
        captureSessionId: getCaptureSessionId(),
        wfhId: wfh.id,
      }).unwrap();
    } catch (err) {
      releaseTabLock();
      if (err?.status === 409) {
        setStatus('elsewhere');
      }
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: 'monitor' },
        audio: false,
        preferCurrentTab: false,
      });
      const videoTrack = stream.getVideoTracks()[0];
      const surface = videoTrack?.getSettings?.().displaySurface;
      if (surface && surface !== 'monitor') {
        showWarningToast('Please share your entire screen, not a window or tab.');
      }

      streamRef.current = stream;
      recordingModeRef.current = claimResponse.recordingMode || getRecordingMode(wfh);
      stopContinuousRef.current = false;
      partIndexRef.current = Number.isFinite(claimResponse.nextPartIndex)
        ? claimResponse.nextPartIndex
        : 0;

      videoTrack.addEventListener('ended', () => {
        showWarningToast('Screen sharing stopped. Recording paused until you share again.');
        stopRecording(true);
      });

      setStatus('capturing');
      startHeartbeat();
      await drainUploadQueue();

      if (recordingModeRef.current === RECORDING_MODES.continuous) {
        startContinuousChunk();
      } else {
        scheduleSlots();
      }
    } catch (err) {
      await stopRecording(true);
    }
  }, [
    claimRecording,
    drainUploadQueue,
    scheduleSlots,
    startContinuousChunk,
    startHeartbeat,
    stopRecording,
  ]);

  useEffect(() => {
    drainUploadQueue();
    const onOnline = () => {
      drainUploadQueue();
    };
    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('online', onOnline);
    };
  }, [drainUploadQueue]);

  useEffect(() => {
    if (isPunchedOut) {
      recordedSlotsRef.current = new Set();
      if (statusRef.current === 'capturing') {
        stopRecording(true);
      } else if (statusRef.current !== 'idle') {
        setStatus('idle');
      }
    }
  }, [isPunchedOut, stopRecording]);

  useEffect(() => {
    if (isPunchedOut || !openWfh || statusRef.current === 'capturing') {
      return;
    }

    if (!openWfh.recordingClaimActive) {
      if (statusRef.current === 'elsewhere') {
        setStatus('idle');
      }
      return;
    }

    let cancelled = false;
    heartbeatRecording({ captureSessionId: getCaptureSessionId() })
      .unwrap()
      .then(() => {
        if (!cancelled && statusRef.current === 'elsewhere') {
          setStatus('idle');
        }
      })
      .catch((err) => {
        if (!cancelled && err?.status === 409) {
          setStatus('elsewhere');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isPunchedOut, openWfh?.id, openWfh?.recordingClaimActive, heartbeatRecording]);

  useEffect(() => {
    const needsReminder = !isPunchedOut
      && openWfh
      && status !== 'capturing'
      && status !== 'elsewhere';

    if (!needsReminder) {
      return undefined;
    }

    const remind = async () => {
      if (statusRef.current === 'capturing' || statusRef.current === 'elsewhere') {
        return;
      }
      await showScreenShareNotification();
      showWarningToast('Please share your screen. Screen recording is required while punched in.');
    };

    const timerId = setInterval(remind, SCREEN_SHARE_REMINDER_MS);

    return () => {
      clearInterval(timerId);
    };
  }, [isPunchedOut, openWfh?.id, status]);

  useEffect(() => {
    const onUnload = () => {
      releaseTabLock();
    };
    window.addEventListener('beforeunload', onUnload);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      clearTimers();
      stopTracks();
      releaseTabLock();
    };
  }, []);

  return (
    <ScreenRecordingContext.Provider
      value={{
        status: !supportsDisplayCapture() && !isPunchedOut ? 'unsupported' : status,
        startRecording,
        stopRecording,
        supportsCapture: supportsDisplayCapture(),
        isPunchedOut,
        openWfh,
      }}
    >
      {children}
    </ScreenRecordingContext.Provider>
  );
};

export default ScreenRecordingProvider;
